/**
 * DesignSplitterNode - Editable Splitter node for network design
 */
import React, { useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from '@xyflow/react';
import { SplitterType } from '../../types';
import { getSplitterLoss, getSplitterOutputs, getSplitterOutputLoss } from '../../utils/splitterCatalog';
import { useSplitterCatalogStore } from '../../stores/splitterCatalogStore';

export interface DesignSplitterNodeData {
  label: string;
  type: SplitterType;
  loss: number;
  inputPower?: number;
  outputPowers?: number[];
}

const SPLITTER_LOSS: Record<SplitterType, number> = {
  '1:2': 3.5,
  '1:4': 7.0,
  '1:8': 10.5,
  '1:16': 13.5,
  '1:32': 17.0,
  '1:64': 20.5,
};

export const DesignSplitterNode: React.FC<NodeProps> = ({ id, data: nodeData, selected }) => {
  const data = nodeData as unknown as DesignSplitterNodeData;
  const { updateNodeData, deleteElements } = useReactFlow();
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [labelValue, setLabelValue] = useState(data.label);

  const handleTypeChange = (newType: SplitterType) => {
    updateNodeData(id as string, {
      ...data,
      type: newType,
      loss: getSplitterLoss(newType),
    });
  };

  const handleLabelChange = () => {
    updateNodeData(id as string, {
      ...data,
      label: labelValue,
    });
    setIsEditingLabel(false);
  };

  const handleDelete = () => {
    if (window.confirm(`Delete ${data.label}?`)) {
      deleteElements({ nodes: [{ id: id as string }] });
    }
  };

  const getSplitterRatio = (type: SplitterType): number => {
    return getSplitterOutputs(type);
  };

  const splitterCatalog = useSplitterCatalogStore((s) => s.types).filter((t) => t.is_active !== false);

  const ratio = getSplitterRatio(data.type);
  // Výkon konkrétneho výstupu = vstup − strata TOHTO výstupu (pre asymetriu rôzna, pre symetriu rovnaká)
  const outputPowerAt = (i: number): number | undefined =>
    data.inputPower !== undefined ? data.inputPower - getSplitterOutputLoss(data.type, i) : undefined;
  // Súhrnný "Output" ukazuje výstup #1 (hore)
  const outputPower = outputPowerAt(0);

  // Get signal quality color
  const getSignalColor = (power?: number) => {
    if (!power) return '#999';
    if (power >= -8) return '#4caf50';
    if (power >= -15) return '#ffeb3b';
    if (power >= -25) return '#ff9800';
    return '#f44336';
  };

  return (
    <div className={`design-node design-splitter-node ${selected ? 'selected' : ''}`}>
      <div className="design-node-header">
        <span>🔀</span>
        {isEditingLabel ? (
          <input
            type="text"
            value={labelValue}
            onChange={(e) => setLabelValue(e.target.value)}
            onBlur={handleLabelChange}
            onKeyDown={(e) => {
              e.stopPropagation(); // Prevent node deletion when typing
              if (e.key === 'Enter') handleLabelChange();
              if (e.key === 'Escape') {
                setLabelValue(data.label);
                setIsEditingLabel(false);
              }
            }}
            autoFocus
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
              outline: 'none',
              width: '120px',
            }}
          />
        ) : (
          <span
            onDoubleClick={() => setIsEditingLabel(true)}
            style={{ cursor: 'text' }}
            title="Dvojklik na úpravu"
          >
            {data.label}
          </span>
        )}
        <button
          onClick={handleDelete}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            fontSize: '16px',
            padding: '2px 4px',
            marginLeft: 'auto',
          }}
          title="Zmazať splitter"
        >
          🗑️
        </button>
      </div>
      <div className="design-node-body">
        <div className="design-field">
          <span className="design-field-label">Typ:</span>
          <select
            className="design-field-select"
            value={data.type}
            onChange={(e) => handleTypeChange(e.target.value as SplitterType)}
          >
            {splitterCatalog.map((t) => (
              <option key={t.id ?? t.name} value={t.name}>{t.name}</option>
            ))}
          </select>
        </div>
        <div className="design-field">
          <span className="design-field-label">Loss:</span>
          <span className="design-field-value">{data.loss} dB</span>
        </div>
        <div className="design-field">
          <span className="design-field-label">Outputs:</span>
          <span className="design-field-value">{ratio}</span>
        </div>
        {data.inputPower !== undefined && (
          <div className="design-field">
            <span className="design-field-label">Vstup:</span>
            <span className="design-field-value" style={{ color: getSignalColor(data.inputPower) }}>
              {data.inputPower.toFixed(2)} dBm
            </span>
          </div>
        )}
        {outputPower !== undefined && (
          <div className="design-field">
            <span className="design-field-label">Výstup:</span>
            <span className="design-field-value" style={{ color: getSignalColor(outputPower) }}>
              {outputPower.toFixed(2)} dBm
            </span>
          </div>
        )}
      </div>
      
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        style={{ background: '#ff9800' }}
      />
      
      {/* Output handles with signal indicators */}
      {Array.from({ length: ratio }).map((_, index) => {
        const spacing = 100 / (ratio + 1);
        const topPosition = spacing * (index + 1);
        const portPower = outputPowerAt(index); // výkon TOHTO výstupu

        return (
          <React.Fragment key={`output-${index}`}>
            <Handle
              type="source"
              position={Position.Right}
              id={`output-${index}`}
              style={{
                top: `${topPosition}%`,
                background: portPower !== undefined ? getSignalColor(portPower) : '#ff9800',
              }}
              title={portPower !== undefined ? `Output ${index + 1}: ${portPower.toFixed(2)} dBm` : `Output ${index + 1}`}
            />
            {portPower !== undefined && (
              <div
                style={{
                  position: 'absolute',
                  right: '-70px',
                  top: `calc(${topPosition}% - 10px)`,
                  fontSize: '9px',
                  background: getSignalColor(portPower),
                  color: portPower >= -15 ? '#000' : '#fff',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                  pointerEvents: 'none',
                }}
              >
                {portPower.toFixed(1)} dBm
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

